// src/api/invites.js
/**
 * Dashboard invite (onboarding) API client (DASH-AUTH-2).
 *
 * Talks to the regional API gateway, which proxies to the backend Organisation
 * Service. Redemption creates an organisation membership and projects it onto
 * the user's account context, so the dashboard access gate authorises the user.
 *
 * Replaces the legacy `redeemClinicianInvite` Firebase Cloud Function callable.
 */

import api from "./axiosInstance";

export const REDEEM_INVITE_PATH = "/v1/dashboard/invites/redeem";
export const CREATE_INVITE_PATH = "/v1/dashboard/invites";
export const UPDATE_INVITE_PATH_PREFIX = "/v1/dashboard/invites/";
export const UPDATE_MEMBERSHIP_PATH_PREFIX = "/v1/dashboard/memberships/";

/**
 * Creates a dashboard onboarding invite (org admin / bootstrap admin only; the
 * backend authorises). Returns the created invite, including its access code.
 *
 * @param {{ email: string, organisationId: string, organisationRole?: string, expiryDays?: number }} payload
 * @returns {Promise<{ invite: { code: string, email: string, organisationId: string, organisationRole: string, expiresAt: string } }>}
 * @throws {import('axios').AxiosError}
 */
export async function createInvite({ email, organisationId, organisationRole, expiryDays }) {
  const { data } = await api.post(CREATE_INVITE_PATH, {
    email: String(email || "").trim(),
    organisationId: String(organisationId || "").trim(),
    ...(organisationRole ? { organisationRole } : {}),
    ...(expiryDays ? { expiryDays } : {}),
  });
  return data;
}

/**
 * Redeems a dashboard onboarding invite for the authenticated user.
 *
 * @param {string} code - The invite/access code.
 * @returns {Promise<{ status: string, membership: object }>} Redemption result.
 * @throws {import('axios').AxiosError} On invalid/expired/used/mismatched codes.
 */
export async function redeemInvite(code) {
  const { data } = await api.post(REDEEM_INVITE_PATH, {
    code: String(code || "").trim(),
  });
  return data;
}

/**
 * Fetches invite summaries visible to the caller.
 *
 * Org admins receive their own organisation's invites; bootstrap admins may
 * optionally scope the list to a provided organisation id.
 *
 * @param {{ organisationId?: string, signal?: AbortSignal }} [options={}]
 * @returns {Promise<{ invites: object[] }>}
 */
export async function fetchInvites({ organisationId, signal } = {}) {
  const { data } = await api.get(CREATE_INVITE_PATH, {
    signal,
    params: organisationId
      ? { organisationId: String(organisationId).trim() }
      : undefined,
  });
  return data;
}

/**
 * Updates an existing invite before it has been redeemed.
 *
 * Supported updates:
 *   • `organisationRole`
 *   • `extendExpiryDays`
 *
 * @param {string} code - Invite code to update.
 * @param {{ organisationRole?: string, extendExpiryDays?: number }} payload
 * @returns {Promise<{ invite: object }>}
 */
export async function updateInvite(code, payload) {
  const normalizedCode = String(code || "").trim();
  const requestBody = {};

  if (payload?.organisationRole) {
    requestBody.organisationRole = payload.organisationRole;
  }

  if (payload?.extendExpiryDays) {
    requestBody.extendExpiryDays = payload.extendExpiryDays;
  }

  const { data } = await api.patch(
    `${UPDATE_INVITE_PATH_PREFIX}${encodeURIComponent(normalizedCode)}`,
    requestBody,
  );
  return data;
}

/**
 * Updates the live organisation role for a redeemed member.
 *
 * @param {string} uid - Redeemed member Firebase UID.
 * @param {{ organisationRole: string }} payload
 * @returns {Promise<{ membership: object, memberUid: string }>}
 */
export async function updateMembershipRole(uid, payload) {
  const normalizedUid = String(uid || "").trim();
  const requestBody = {};

  if (payload?.organisationRole) {
    requestBody.organisationRole = payload.organisationRole;
  }

  const { data } = await api.patch(
    `${UPDATE_MEMBERSHIP_PATH_PREFIX}${encodeURIComponent(normalizedUid)}`,
    requestBody,
  );
  return data;
}

/**
 * Extracts the backend error code from a failed invite request, if present.
 *
 * @param {unknown} error - Caught error (typically an AxiosError).
 * @returns {string|null}
 */
export function inviteErrorCode(error) {
  return error?.response?.data?.error?.code ?? null;
}
